import React, { useEffect, useState } from "react";
import { Route, useHistory, useLocation } from "react-router";

import "@rmwc/icon-button/styles";
import useLayout from "hooks/useLayout";
import { PageHeader } from "components/PageHeader";
import { PageMain, PageContent } from "components/PageStyles";
import { usePart } from "hooks/params/usePart";
import * as Pages from "../../../../../..";
import { TabBar, Tab } from "@rmwc/tabs";
import { TabContainer } from "components/TabContainer";
import { DisplayFlex } from "components/Utilities";
import { AttachmentList } from "components/attachments/AttachmentList";
import { AttachmentFormDialog } from "components/attachments/AttachmentFormDialog";
import { Switch } from "react-router-dom";
import { PartsDetailView } from "components/parts/PartsDetailView";
import useTranslations from "hooks/useTranslations";
import styled from "styled-components";

const AttachmentsListWrapper = styled.div`
  > * + * {
    margin-top: 2rem;
  }
`;

export const SubSubPartDetailPage: React.FC = () => {
  const { tCommon } = useTranslations();
  const history = useHistory();
  const { currentPart, parentPart, part, subPart, subSubPart, partId, subPartId, subSubPartId } = usePart();
  const location = useLocation();
  const { setBreadCrumbs } = useLayout();

  const PAGE_TABS: { icon: string; label: string; path: string }[] = [];
  if (currentPart) {
    PAGE_TABS.push({
      icon: "info",
      label: "Details",
      path: `/parts/${partId}/parts/${subPartId}/parts/${subSubPartId}/details`,
    });

    PAGE_TABS.push({
      icon: "upload_file",
      label: "Attachments",
      path: `/parts/${partId}/parts/${subPartId}/parts/${subSubPartId}/attachments`,
    });
  }

  const [activeTabIndex, setActiveTabIndex] = useState<number>(
    PAGE_TABS.findIndex((it) => location.pathname.startsWith(it.path)),
  );

  useEffect(() => {
    if (location.pathname.endsWith("/edit")) return;
    const tabIndex = PAGE_TABS.findIndex((it) => location.pathname.startsWith(it.path));
    if (tabIndex === activeTabIndex) return;
    setActiveTabIndex(tabIndex === -1 ? 0 : tabIndex);
  }, [location, PAGE_TABS]);

  useEffect(() => {
    if (!setBreadCrumbs) return;
    if (!part || !subPart || !subSubPart) return;

    setBreadCrumbs([
      { label: tCommon("Parts"), path: "/parts" },
      { label: part.name, path: `/parts/${partId}/parts` },
      { label: subPart.name, path: `/parts/${partId}/parts/${subPartId}/parts` },
      {
        label: `SN: ${subSubPart.serialNumber}`,
        path: `/parts/${partId}/parts/${subPartId}/parts/${subSubPartId}/parts`,
      },
    ]);
  }, [setBreadCrumbs, currentPart]);

  const onTabActivated = (ev: CustomEvent) => {
    const { path } = PAGE_TABS[ev.detail.index];
    if (location.pathname.startsWith(path) || location.pathname.endsWith("/edit")) return;
    history.replace(path);
  };

  if (!currentPart) return <Route component={Pages.Error404} />;

  return (
    <>
      <Route path="/parts/:partId/parts/:subPartId/parts/:subSubPartId/attachments/new">
        <AttachmentFormDialog action="create" />
      </Route>

      <PageHeader title={currentPart.name} description={currentPart.type} />

      <TabContainer>
        <TabBar {...{ activeTabIndex: activeTabIndex === -1 ? 0 : activeTabIndex }} onActivate={onTabActivated}>
          {PAGE_TABS.map((it, i) => (
            <Tab key={i} icon={it.icon}>
              <DisplayFlex alignItems="center">
                <DisplayFlex>{it.label}</DisplayFlex>
              </DisplayFlex>
            </Tab>
          ))}
        </TabBar>
      </TabContainer>

      <PageMain>
        <PageContent>
          <Switch>
            <Route path="/parts/:partId/parts/:subPartId/parts/:subSubPartId/details">
              <PartsDetailView />
            </Route>

            <Route path="/parts/:partId/parts/:subPartId/parts/:subSubPartId/attachments">
              <AttachmentsListWrapper>
                <AttachmentList type="part" items={currentPart.attachments?.items || []} />
                {(parentPart?.attachments?.items?.length || 0) > 0 && (
                  <AttachmentList type="parentPart" items={parentPart?.attachments?.items || []} />
                )}
              </AttachmentsListWrapper>
            </Route>
          </Switch>
        </PageContent>
      </PageMain>
    </>
  );
};
